import React from "react";
import "./footer-actions.scss";

/** Single action button config for FooterActions */
export interface FooterActionItem {
  /** Button label */
  label: string;
  /** Click handler */
  onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
  /** Visual variant: primary (filled) or secondary (outline) */
  variant: "primary" | "secondary";
  /** Disabled state */
  disabled?: boolean;
  /** Optional icon or element before the label */
  startIcon?: React.ReactNode;
  /** Optional icon or element after the label */
  endIcon?: React.ReactNode;
  /** Accessible label for the button */
  ariaLabel?: string;
  /** Button type. Default "button". */
  type?: "button" | "submit" | "reset";
}

export interface FooterActionsProps {
  /**
   * Primary action (right-most in LTR; typically "Apply", "Save", "Submit").
   * Rendered as filled (dark) button.
   */
  primaryAction: {
    label: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    ariaLabel?: string;
    type?: "button" | "submit" | "reset";
  };
  /**
   * Optional secondary action (e.g. "Cancel").
   * Rendered as outline button. Omit to show only the primary button.
   */
  secondaryAction?: {
    label: string;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
    disabled?: boolean;
    startIcon?: React.ReactNode;
    endIcon?: React.ReactNode;
    ariaLabel?: string;
    type?: "button" | "submit" | "reset";
  };
  /**
   * Alternatively pass an array of actions for full control (order preserved).
   * When provided, primaryAction and secondaryAction are ignored.
   */
  actions?: FooterActionItem[];
  /**
   * Horizontal alignment of the button group.
   * - "end": buttons aligned to the right (e.g. for modals)
   * - "center": centered
   * - "start": aligned to the left
   * @default "end"
   */
  align?: "start" | "center" | "end";
  /**
   * When true, buttons share width equally (flex: 1). When false, buttons are auto-width.
   * @default true
   */
  fullWidthButtons?: boolean;
  /** Padding override: [vertical, horizontal] in rem. Default [0.75, 1] (12px, 16px) from Figma. */
  padding?: [number, number];
  /** Whether to show the top border (separator). @default true */
  showTopBorder?: boolean;
  /** Root element class name */
  className?: string;
  /** Root element inline style */
  style?: React.CSSProperties;
}

/**
 * Footer action bar matching Figma Pharmacy Prior Auth design (node 1891-38477).
 * Renders a row of primary/secondary buttons with consistent spacing and styling.
 */
export const FooterActions: React.FC<FooterActionsProps> = ({
  primaryAction,
  secondaryAction,
  actions: actionsProp,
  align = "end",
  fullWidthButtons = true,
  padding,
  showTopBorder = true,
  className = "",
  style,
}) => {
  const paddingVertical = padding ? padding[0] : 0.75;
  const paddingHorizontal = padding ? padding[1] : 1;

  const resolvedActions: FooterActionItem[] = actionsProp
    ? actionsProp
    : [
        ...(secondaryAction
          ? [
              {
                label: secondaryAction.label,
                onClick: secondaryAction.onClick,
                disabled: secondaryAction.disabled,
                startIcon: secondaryAction.startIcon,
                endIcon: secondaryAction.endIcon,
                ariaLabel: secondaryAction.ariaLabel,
                type: secondaryAction.type ?? "button",
                variant: "secondary" as const,
              },
            ]
          : []),
        {
          label: primaryAction.label,
          onClick: primaryAction.onClick,
          disabled: primaryAction.disabled,
          startIcon: primaryAction.startIcon,
          endIcon: primaryAction.endIcon,
          ariaLabel: primaryAction.ariaLabel,
          type: primaryAction.type ?? "button",
          variant: "primary" as const,
        },
      ];

  return (
    <div
      className={`footer-actions ${showTopBorder ? "footer-actions--bordered" : ""} footer-actions--align-${align} ${className}`}
      style={{
        padding: `${paddingVertical}rem ${paddingHorizontal}rem`,
        ...style,
      }}
    >
      <div
        className={`footer-actions__group ${fullWidthButtons ? "footer-actions__group--full-width" : ""}`}
      >
        {resolvedActions.map((action, index) => (
          <button
            key={index}
            type={action.type ?? "button"}
            className={`footer-actions__btn footer-actions__btn--${action.variant}`}
            onClick={action.onClick}
            disabled={action.disabled}
            aria-label={action.ariaLabel ?? action.label}
          >
            {action.startIcon && (
              <span className="footer-actions__btn-icon" aria-hidden>
                {action.startIcon}
              </span>
            )}
            <span className="footer-actions__btn-label">{action.label}</span>
            {action.endIcon && (
              <span className="footer-actions__btn-icon" aria-hidden>
                {action.endIcon}
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
